'use strict';

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = ToolbarPlugin;
var _reactDom = require("react-dom");
var _react = _interopRequireWildcard(require("react"));
var _lexical = require("lexical");
var _link = require("@lexical/link");
var _list = require("@lexical/list");
var _LexicalComposerContext = require("@lexical/react/LexicalComposerContext");
var _richText = require("@lexical/rich-text");
var _selection = require("@lexical/selection");
var _table = require("@lexical/table");
var _utils = require("@lexical/utils");
var _useModal = _interopRequireDefault(require("../hooks/useModal"));
var _TextInput = _interopRequireDefault(require("../ui/TextInput"));
var _DropDown = _interopRequireDefault(require("../ui/DropDown"));
var _Button = _interopRequireDefault(require("../ui/Button"));
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const {
  app
} = window;
const supportedBlockTypes = new Set(['paragraph', 'quote', 'h1', 'h2', 'h3', 'ul', 'ol']);
const blockTypeToBlockName = {
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  ol: 'Numbered List',
  paragraph: 'Normal',
  quote: 'Quote',
  ul: 'Bulleted List'
};
function getSelectedNode(selection) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) return anchorNode;
  const isBackward = selection.isBackward();
  if (isBackward) return (0, _selection.$isAtNodeEnd)(focus) ? anchorNode : focusNode;
  return (0, _selection.$isAtNodeEnd)(anchor) ? focusNode : anchorNode;
}
function positionEditorElement(editor, rect) {
  if (rect === null) {
    editor.style.opacity = '0';
    editor.style.top = '-1000px';
    editor.style.left = '-1000px';
  } else {
    const window_width = window.innerWidth;
    const tooltip_width = editor.offsetWidth;
    const text_width = rect.width;
    editor.style.opacity = '1';
    editor.style.top = rect.top + rect.height + window.pageYOffset + 10 + 'px';
    let left_pos = rect.left + window.pageXOffset;
    left_pos = left_pos - tooltip_width / 2 + text_width / 2;
    if (left_pos < 5) left_pos = 5; // 5px from window left border minimum

    let right_pos = window_width - left_pos - tooltip_width;
    if (right_pos < 5)
      // 5px from window right border minimum
      {
        if (right_pos < 0) {
          left_pos += right_pos - 5;
        } else {
          left_pos -= 5 - right_pos;
        }
      }
    editor.style.left = left_pos + 'px';
  }
}
function FloatingLinkEditor(_ref) {
  let {
    editor
  } = _ref;
  const editorRef = (0, _react.useRef)(null);
  const inputRef = (0, _react.useRef)(null);
  const mouseDownRef = (0, _react.useRef)(false);
  const [linkUrl, setLinkUrl] = (0, _react.useState)('');
  const [isEditMode, setEditMode] = (0, _react.useState)(false);
  const [lastSelection, setLastSelection] = (0, _react.useState)(null);
  const updateLinkEditor = (0, _react.useCallback)(() => {
    const selection = (0, _lexical.$getSelection)();
    if ((0, _lexical.$isRangeSelection)(selection)) {
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ((0, _link.$isLinkNode)(parent)) {
        setLinkUrl(parent.getURL());
      } else if ((0, _link.$isLinkNode)(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl('');
      }
    }
    const editorElem = editorRef.current;
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;
    if (editorElem === null) return;
    const rootElement = editor.getRootElement();
    if (selection !== null && !nativeSelection.isCollapsed && rootElement !== null && rootElement.contains(nativeSelection.anchorNode)) {
      const domRange = nativeSelection.getRangeAt(0);
      let rect;
      if (nativeSelection.anchorNode === rootElement) {
        let inner = rootElement;
        while (inner.firstElementChild != null) {
          inner = inner.firstElementChild;
        }
        rect = inner.getBoundingClientRect();
      } else {
        rect = domRange.getBoundingClientRect();
      }
      if (!mouseDownRef.current) {
        positionEditorElement(editorElem, rect);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== 'link-input') {
      positionEditorElement(editorElem, null);
      setLastSelection(null);
      setEditMode(false);
      setLinkUrl('');
    }
    return true;
  }, [editor]);
  (0, _react.useEffect)(() => {
    return (0, _utils.mergeRegister)(editor.registerUpdateListener(_ref2 => {
      let {
        editorState
      } = _ref2;
      editorState.read(() => updateLinkEditor());
    }), editor.registerCommand(_lexical.SELECTION_CHANGE_COMMAND, () => {
      updateLinkEditor();
      return true;
    }, _lexical.COMMAND_PRIORITY_LOW));
  }, [editor, updateLinkEditor]);
  (0, _react.useEffect)(() => {
    editor.getEditorState().read(() => updateLinkEditor());
  }, [editor, updateLinkEditor]);
  (0, _react.useEffect)(() => {
    if (isEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditMode]);
  const output = /*#__PURE__*/_react.default.createElement("div", {
    ref: editorRef,
    className: "link-editor"
  }, isEditMode ? /*#__PURE__*/_react.default.createElement(_react.Fragment, null, /*#__PURE__*/_react.default.createElement("input", {
    ref: inputRef,
    className: "link-input",
    value: linkUrl,
    onChange: e => setLinkUrl(e.target.value),
    onKeyDown: event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        validate();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        setEditMode(false);
      }
    }
  }), /*#__PURE__*/_react.default.createElement("button", {
    className: "link-validate",
    type: "button",
    onMouseDown: e => e.preventDefault(),
    onClick: validate,
    tabIndex: 0
  }, "OK")) : /*#__PURE__*/_react.default.createElement(_react.Fragment, null, /*#__PURE__*/_react.default.createElement("div", {
    className: "link-input"
  }, /*#__PURE__*/_react.default.createElement("a", {
    href: linkUrl,
    target: "_blank",
    rel: "noopener noreferrer",
    title: "Ouvrir dans un nouvel onglet"
  }, linkUrl), /*#__PURE__*/_react.default.createElement("button", {
    className: "link-edit",
    type: "button",
    tabIndex: 0,
    onMouseDown: e => e.preventDefault(),
    onClick: () => setEditMode(true)
  }))));
  return output;
  function validate() {
    if (lastSelection !== null) {
      if (linkUrl !== '') {
        editor.dispatchCommand(_link.TOGGLE_LINK_COMMAND, linkUrl);
      } else {
        editor.dispatchCommand(_link.TOGGLE_LINK_COMMAND, null);
      }
      setEditMode(false);
    }
  }
}
function InsertTableDialog(_ref3) {
  let {
    activeEditor,
    onClose
  } = _ref3;
  const [columns, setColumns] = (0, _react.useState)('5');
  const [rows, setRows] = (0, _react.useState)('5');
  (0, _react.useEffect)(() => {
    activeEditor.focus();
    activeEditor.update(() => {
      const selection = (0, _lexical.$getSelection)();
      if (selection !== null) return;
      const root = (0, _lexical.$getRoot)();
      root.selectEnd();
    });
  }, []);
  const onClick = () => {
    activeEditor.dispatchCommand(_table.INSERT_TABLE_COMMAND, {
      columns,
      rows
    });
    onClose && onClose();
  };
  const output = /*#__PURE__*/_react.default.createElement(_react.Fragment, null, /*#__PURE__*/_react.default.createElement(_TextInput.default, {
    label: "Nb de lignes",
    onChange: setRows,
    value: rows
  }), /*#__PURE__*/_react.default.createElement(_TextInput.default, {
    label: "Nb de colonnes",
    onChange: setColumns,
    value: columns
  }), /*#__PURE__*/_react.default.createElement("div", {
    className: "ToolbarPlugin__dialogActions",
    "data-test-id": "table-model-confirm-insert"
  }, /*#__PURE__*/_react.default.createElement(_Button.default, {
    onClick: onClick
  }, "Confirmer")));
  return output;
}
function BlockFormatDropDown(_ref4) {
  let {
    editor,
    blockType
  } = _ref4;
  const formatParagraph = () => {
    if (blockType !== 'paragraph') {
      editor.update(() => {
        const selection = (0, _lexical.$getSelection)();
        if ((0, _lexical.$isRangeSelection)(selection)) {
          (0, _selection.$wrapLeafNodesInElements)(selection, () => (0, _lexical.$createParagraphNode)());
        }
      });
    }
  };
  const formatHeading = headingSize => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = (0, _lexical.$getSelection)();
        if ((0, _lexical.$isRangeSelection)(selection)) {
          (0, _selection.$wrapLeafNodesInElements)(selection, () => (0, _richText.$createHeadingNode)(headingSize));
        }
      });
    }
  };
  const formatBulletList = () => {
    if (blockType !== 'ul') {
      editor.dispatchCommand(_list.INSERT_UNORDERED_LIST_COMMAND);
    } else {
      editor.dispatchCommand(_list.REMOVE_LIST_COMMAND);
    }
  };
  const formatNumberedList = () => {
    if (blockType !== 'ol') {
      editor.dispatchCommand(_list.INSERT_ORDERED_LIST_COMMAND);
    } else {
      editor.dispatchCommand(_list.REMOVE_LIST_COMMAND);
    }
  };
  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = (0, _lexical.$getSelection)();
        if ((0, _lexical.$isRangeSelection)(selection)) {
          (0, _selection.$wrapLeafNodesInElements)(selection, () => (0, _richText.$createQuoteNode)());
        }
      });
    }
  };
  const output = /*#__PURE__*/_react.default.createElement(_DropDown.default, {
    buttonClassName: "toolbar-item block-controls",
    buttonIconClassName: 'icon block-type ' + blockType,
    buttonLabel: blockTypeToBlockName[blockType],
    buttonAriaLabel: "Options de formattage"
  }, /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: formatParagraph
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "icon paragraph"
  }), /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Normal"), blockType === 'paragraph' && /*#__PURE__*/_react.default.createElement("span", {
    className: "active"
  })), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: () => formatHeading('h1')
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "icon h1"
  }), /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Titre principal"), blockType === 'h1' && /*#__PURE__*/_react.default.createElement("span", {
    className: "active"
  })), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: () => formatHeading('h2')
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "icon h2"
  }), /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Titre secondaire"), blockType === 'h2' && /*#__PURE__*/_react.default.createElement("span", {
    className: "active"
  })), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: () => formatHeading('h3')
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "icon h3"
  }), /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Sous-titre"), blockType === 'h3' && /*#__PURE__*/_react.default.createElement("span", {
    className: "active"
  })), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: formatBulletList
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "icon bullet-list"
  }), /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Liste \xE0 puce"), blockType === 'ul' && /*#__PURE__*/_react.default.createElement("span", {
    className: "active"
  })), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: formatNumberedList
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "icon numbered-list"
  }), /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Liste num\xE9rot\xE9e"), blockType === 'ol' && /*#__PURE__*/_react.default.createElement("span", {
    className: "active"
  })), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "item",
    onClick: formatQuote
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: "icon quote"
  }), /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Citation"), blockType === 'quote' && /*#__PURE__*/_react.default.createElement("span", {
    className: "active"
  })));
  return output;
}
function Divider() {
  return /*#__PURE__*/_react.default.createElement("div", {
    className: "divider"
  });
}
function Select(_ref5) {
  let {
    onChange,
    className,
    options,
    value
  } = _ref5;
  const output = /*#__PURE__*/_react.default.createElement("select", {
    className: className,
    onChange: onChange,
    value: value
  }, options.map(_ref6 => {
    let [option, text] = _ref6;
    return /*#__PURE__*/_react.default.createElement("option", {
      key: option,
      value: option
    }, text);
  }));
  return output;
}
function ToolbarPlugin(props) {
  const [editor] = (0, _LexicalComposerContext.useLexicalComposerContext)();
  const [activeEditor, setActiveEditor] = (0, _react.useState)(editor);
  const [blockType, setBlockType] = (0, _react.useState)('paragraph');
  const [fontSize, setFontSize] = (0, _react.useState)('1em');
  const [isLink, setIsLink] = (0, _react.useState)(false);
  const [isBold, setIsBold] = (0, _react.useState)(false);
  const [isItalic, setIsItalic] = (0, _react.useState)(false);
  const [isUnderline, setIsUnderline] = (0, _react.useState)(false);
  const [isStrikethrough, setIsStrikethrough] = (0, _react.useState)(false);
  const [canUndo, setCanUndo] = (0, _react.useState)(false);
  const [canRedo, setCanRedo] = (0, _react.useState)(false);
  const [isRTL, setIsRTL] = (0, _react.useState)(false);
  const [modal, showModal] = (0, _useModal.default)();
  const updateToolbar = (0, _react.useCallback)(() => {
    const selection = (0, _lexical.$getSelection)();
    if ((0, _lexical.$isRangeSelection)(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element = anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsRTL((0, _selection.$isParentElementRTL)(selection));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ((0, _link.$isLinkNode)(parent) || (0, _link.$isLinkNode)(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }
      if (elementDOM !== null) {
        if ((0, _list.$isListNode)(element)) {
          const parentList = (0, _utils.$getNearestNodeOfType)(anchorNode, _list.ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = (0, _richText.$isHeadingNode)(element) ? element.getTag() : element.getType();
          setBlockType(type);
        }
      }

      // Hande buttons
      setFontSize((0, _selection.$getSelectionStyleValueForProperty)(selection, 'font-size', '1em'));
    }
  }, [activeEditor]);
  (0, _react.useEffect)(() => {
    return editor.registerCommand(_lexical.SELECTION_CHANGE_COMMAND, (_payload, newEditor) => {
      updateToolbar();
      setActiveEditor(newEditor);
      return false;
    }, _lexical.COMMAND_PRIORITY_CRITICAL);
  }, [editor, updateToolbar]);
  (0, _react.useEffect)(() => {
    return (0, _utils.mergeRegister)(activeEditor.registerUpdateListener(_ref7 => {
      let {
        editorState
      } = _ref7;
      editorState.read(() => updateToolbar());
    }), activeEditor.registerCommand(_lexical.CAN_UNDO_COMMAND, payload => {
      setCanUndo(payload);
      return false;
    }, _lexical.COMMAND_PRIORITY_CRITICAL), activeEditor.registerCommand(_lexical.CAN_REDO_COMMAND, payload => {
      setCanRedo(payload);
      return false;
    }, _lexical.COMMAND_PRIORITY_CRITICAL));
  }, [activeEditor, updateToolbar]);
  const applyStyleText = (0, _react.useCallback)(styles => {
    activeEditor.update(() => {
      const selection = (0, _lexical.$getSelection)();
      if ((0, _lexical.$isRangeSelection)(selection)) {
        (0, _selection.$patchStyleText)(selection, styles);
      }
    });
  }, [activeEditor]);
  const onFontSizeSelect = (0, _react.useCallback)(e => applyStyleText({
    'font-size': e.target.value
  }), [applyStyleText]);
  const insertLink = (0, _react.useCallback)(() => {
    if (!isLink) {
      editor.dispatchCommand(_link.TOGGLE_LINK_COMMAND, 'https://');
    } else {
      editor.dispatchCommand(_link.TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);
  const formatter_commands = props['disable-floating-formatter'] ? /*#__PURE__*/_react.default.createElement(_react.Fragment, null, /*#__PURE__*/_react.default.createElement(Divider, null), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    onClick: () => activeEditor.dispatchCommand(_lexical.FORMAT_TEXT_COMMAND, 'bold'),
    className: 'toolbar-item spaced ' + (isBold ? 'active' : ''),
    title: "Gras (Ctrl+B)",
    "aria-label": "Passe le texte s\xE9lectionn\xE9 en gras"
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: "format bold"
  })), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    onClick: () => activeEditor.dispatchCommand(_lexical.FORMAT_TEXT_COMMAND, 'italic'),
    className: 'toolbar-item spaced ' + (isItalic ? 'active' : ''),
    title: "Italique",
    "aria-label": "Passe le texte s\xE9lectionn\xE9 en Italique"
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: "format italic"
  })), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    onClick: () => activeEditor.dispatchCommand(_lexical.FORMAT_TEXT_COMMAND, 'underline'),
    className: 'toolbar-item spaced ' + (isUnderline ? 'active' : ''),
    title: "Souligner",
    "aria-label": "Souligne le texte s\xE9lectionn\xE9"
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: "format underline"
  })), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    onClick: () => activeEditor.dispatchCommand(_lexical.FORMAT_TEXT_COMMAND, 'strikethrough'),
    className: 'toolbar-item spaced ' + (isStrikethrough ? 'active' : ''),
    title: "Barrer",
    "aria-label": "Barre le texte s\xE9lectionn\xE9"
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: "format strikethrough"
  })), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    onClick: insertLink,
    className: 'toolbar-item spaced ' + (isLink ? 'active' : ''),
    "aria-label": "Ins\xE9rez un lien",
    title: "Ins\xE9rez un lien"
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: "format link"
  }))) : undefined;
  const toolbar = /*#__PURE__*/_react.default.createElement("div", {
    className: "c-text-editor__toolbar-content"
  }, /*#__PURE__*/_react.default.createElement("button", {
    disabled: !canUndo,
    type: "button",
    onClick: () => activeEditor.dispatchCommand(_lexical.UNDO_COMMAND),
    title: "Annuler (Ctrl+Z)",
    className: "toolbar-item spaced",
    "aria-label": "Annuler"
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: "format undo"
  })), /*#__PURE__*/_react.default.createElement("button", {
    disabled: !canRedo,
    type: "button",
    onClick: () => activeEditor.dispatchCommand(_lexical.REDO_COMMAND),
    title: "Refaire",
    className: "toolbar-item",
    "aria-label": "Refaire"
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: "format redo"
  })), /*#__PURE__*/_react.default.createElement(Divider, null), supportedBlockTypes.has(blockType) && activeEditor === editor ? /*#__PURE__*/_react.default.createElement(_react.Fragment, null, /*#__PURE__*/_react.default.createElement(BlockFormatDropDown, {
    blockType: blockType,
    editor: editor
  }), /*#__PURE__*/_react.default.createElement(Divider, null)) : undefined, /*#__PURE__*/_react.default.createElement(_react.Fragment, null, /*#__PURE__*/_react.default.createElement(Select, {
    className: "toolbar-item font-size",
    onChange: onFontSizeSelect,
    value: fontSize,
    options: [['0.6em', '60%'], ['0.7em', '70%'], ['0.8em', '80%'], ['0.9em', '90%'], ['1em', '100%'], ['1.1em', '110%'], ['1.2em', '120%'], ['1.4em', '140%']]
  }), /*#__PURE__*/_react.default.createElement("i", {
    className: "chevron-down inside"
  })), formatter_commands, isLink ? /*#__PURE__*/(0, _reactDom.createPortal)( /*#__PURE__*/_react.default.createElement(FloatingLinkEditor, {
    editor: activeEditor
  }), document.body) : undefined, /*#__PURE__*/_react.default.createElement(Divider, null), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    className: "toolbar-item",
    onClick: () => {
      showModal('Insérer un tableau', onClose => /*#__PURE__*/_react.default.createElement(InsertTableDialog, {
        activeEditor: activeEditor,
        onClose: onClose
      }));
    }
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: "icon table"
  }), /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Tableau")), /*#__PURE__*/_react.default.createElement(Divider, null), /*#__PURE__*/_react.default.createElement(_DropDown.default, {
    buttonLabel: "Alignement",
    buttonIconClassName: "icon left-align",
    buttonClassName: "toolbar-item spaced alignment",
    buttonAriaLabel: "Alignement"
  }, /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    onClick: () => activeEditor.dispatchCommand(_lexical.FORMAT_ELEMENT_COMMAND, 'left'),
    className: "item"
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: "icon left-align"
  }), /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Aligner \xE0 gauche")), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    onClick: () => activeEditor.dispatchCommand(_lexical.FORMAT_ELEMENT_COMMAND, 'center'),
    className: "item"
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: "icon center-align"
  }), /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Center")), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    onClick: () => activeEditor.dispatchCommand(_lexical.FORMAT_ELEMENT_COMMAND, 'right'),
    className: "item"
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: "icon right-align"
  }), /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Aligner \xE0 droite")), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    onClick: () => activeEditor.dispatchCommand(_lexical.FORMAT_ELEMENT_COMMAND, 'justify'),
    className: "item"
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: "icon justify-align"
  }), /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Justifier")), /*#__PURE__*/_react.default.createElement(Divider, null), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    onClick: () => activeEditor.dispatchCommand(_lexical.OUTDENT_CONTENT_COMMAND),
    className: "item"
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: 'icon ' + (isRTL ? 'indent' : 'outdent')
  }), /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "R\xE9duire l'alin\xE9a")), /*#__PURE__*/_react.default.createElement("button", {
    type: "button",
    onClick: () => activeEditor.dispatchCommand(_lexical.INDENT_CONTENT_COMMAND),
    className: "item"
  }, /*#__PURE__*/_react.default.createElement("i", {
    className: 'icon ' + (isRTL ? 'outdent' : 'indent')
  }), /*#__PURE__*/_react.default.createElement("span", {
    className: "text"
  }, "Augmenter l'alin\xE9a"))));
  const output = /*#__PURE__*/_react.default.createElement("div", {
    className: "c-text-editor__toolbar"
  }, toolbar, props.children, modal);
  return output;
}
